import { useCallback, useState } from "react";
import { useParams } from "react-router";
import _ from "lodash";

import {
  DeleteOutlineOutlinedIcon,
  DownloadOutlinedIcon,
  LaunchIcon,
} from "icons";
import theme from "theme";
import { colors } from "theme/palette";
import { openURLInNewTab } from "utils/common";
import { format, FORMATS, parseJSON } from "utils/datetime";
import { downloadFileWithFileName } from "utils/file";

import Button from "components/Button";
import IconButton from "components/IconButton";
import Stack from "components/Stack";
import Text from "components/Text";
import toast from "components/Toast";
import Tooltip from "components/Tooltip";

import {
  IMemoPDFDocument,
  memoPDFFileWithSignedUrlAndOriginalFileNameRetrieve,
} from "entities/Memo/sdk";

const MemoPDFDocument = ({
  memoPDF,
  handleDelete,
}: {
  memoPDF: IMemoPDFDocument;
  handleDelete?: ({ memoPDFId }: { memoPDFId: number }) => void;
}) => {
  const params = useParams<{
    dealId: string;
    memoId: string;
  }>();
  const dealId = Number(params.dealId);
  const memoId = Number(params.memoId);

  const [isDownloading, setIsDownloading] = useState<boolean>(false);

  const onDownload = useCallback(
    ({ memoPDFId, fileName }: { memoPDFId: number; fileName: string }) => {
      setIsDownloading(true);
      memoPDFFileWithSignedUrlAndOriginalFileNameRetrieve({
        dealId,
        memoId,
        memoPDFId,
      }).then((data) => {
        const toastId = toast.loading("Retrieving file...");

        downloadFileWithFileName({
          fileUrl: data.url,
          fileName,
          toastId,
        }).finally(() => setIsDownloading(false));
      });
    },
    [dealId, memoId]
  );

  const handleMemoPDFFilePreview = useCallback(
    ({ memoPDFId }: { memoPDFId: number }) => {
      memoPDFFileWithSignedUrlAndOriginalFileNameRetrieve({
        dealId,
        memoId,
        memoPDFId,
      }).then(({ url }) => {
        openURLInNewTab({ url });
      });
    },
    [dealId, memoId]
  );
  return (
    <>
      <Stack
        direction="row"
        padding={theme.spacing(1.5, 0)}
        gap={1.5}
        overflow="hidden"
        textOverflow="ellipsis"
      >
        <Tooltip title={memoPDF.file.original_file_name}>
          <Text variant="text4" fontWeight={700} noWrap>
            {memoPDF.file.original_file_name}
          </Text>
        </Tooltip>

        <Tooltip
          title={`Saved at ${format(
            parseJSON(memoPDF.created_at),
            FORMATS.FRONTEND.DATETIME
          )} ${memoPDF.is_created_through_shareable_link ? "from shareable memo link" : `by ${memoPDF.created_by.first_name} ${memoPDF.created_by.last_name}`}.`}
        >
          <Text variant="text4" color={colors.gray80} noWrap minWidth={100}>
            {format(parseJSON(memoPDF.created_at), FORMATS.FRONTEND.DATE)}
          </Text>
        </Tooltip>
      </Stack>
      <Stack direction="row" alignItems="center" gap={1}>
        <Button
          variant="outlined"
          color="secondary"
          sx={{
            padding: theme.spacing(0.2, 1),
            height: "fit-content",
          }}
          onClick={() =>
            handleMemoPDFFilePreview({
              memoPDFId: memoPDF.id,
            })
          }
        >
          <Stack direction="row" alignItems="center" gap={0.5}>
            <LaunchIcon
              sx={{
                width: 16,
                height: 16,
              }}
            />
            <Text variant="text4" fontWeight={500}>
              View
            </Text>
          </Stack>
        </Button>
        <Button
          variant="outlined"
          color="secondary"
          sx={{
            padding: theme.spacing(0.2, 1),
            height: "fit-content",
          }}
          onClick={() =>
            onDownload({
              memoPDFId: memoPDF.id,
              fileName: memoPDF.file.original_file_name,
            })
          }
          disabled={isDownloading}
        >
          <Stack direction="row" alignItems="center" gap={0.5}>
            <DownloadOutlinedIcon
              sx={{
                width: 16,
                height: 16,
              }}
            />
            <Text variant="text4" fontWeight={500}>
              Download
            </Text>
          </Stack>
        </Button>
        <IconButton
          size="small"
          onClick={() =>
            !_.isNil(handleDelete) && handleDelete({ memoPDFId: memoPDF.id })
          }
          sx={{
            alignSelf: "center",
          }}
        >
          <DeleteOutlineOutlinedIcon
            sx={{
              width: 20,
              height: 20,
            }}
          />
        </IconButton>
      </Stack>
    </>
  );
};

export default MemoPDFDocument;
